import { ElMessage, ElMessageBox } from 'element-plus';
import { addErrorHandler } from '@sfvine/main-qiankun';
import type { PluginOptions } from './type';
import { useSettingsStore } from '../stores';

type AppError = Error & { appOrParcelName: string; };

/** 当资源加载404时，用户是否正在选择 */
let isWatingUserChooseOnAssets404 = false

/**
 * 处理静态资源加载404情况，并重新加载
 * 防止蓝绿环境下因为切换灰度导致的资源丢失
 */
const handleAssets404 = async () => {
  if (isWatingUserChooseOnAssets404) {
    return
  }

  const RELOAD_ASSETS_ON_404_CACHE_KEY = 'RELOAD_ASSETS_ON_404_CACHE_KEY'
  const assetsOn404Count = JSON.parse(sessionStorage.getItem(RELOAD_ASSETS_ON_404_CACHE_KEY) || '0')
  if (assetsOn404Count > 0) {
    return
  }

  try {
    isWatingUserChooseOnAssets404 = true
    const res = await ElMessageBox.confirm(
      '检测到资源需要更新，是否更新页面？',
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
        closeOnClickModal: false,
      }
    )
    if (res !== 'confirm') {
      return
    }

    // 重新加载页面
    sessionStorage.setItem(RELOAD_ASSETS_ON_404_CACHE_KEY, assetsOn404Count + 1)
    location.reload()
  } catch(e) {
    // 
  } finally {
    isWatingUserChooseOnAssets404 = false
  }
}

export const errorHandler = (event: AppError | ErrorEvent | PromiseRejectionEvent | Error) => {
  console.warn(event);

  const settingsStore =  useSettingsStore()

  let message: string | undefined;
  switch(true) {
    case 'reason' in event:
      const reason = event.reason;
      // TODO 临时方案
      // 处理 element-ui confirm 组件的取消操作
      if (reason !== 'cancel') {
        message = typeof reason === 'string' ? reason : reason.message;
      }
      break;
    case 'message' in event:
      message = event.message;
      break;
  }

  // 处理 404
  if (!settingsStore.settings.errorConfig.hideAssets404) {
    const whiteListMessageOn404 = [
      'load failed with status 404',
      'Failed to fetch dynamically imported module',
      'Unable to preload'
    ]
    let isAssets404 = whiteListMessageOn404.some(item => message?.includes(item))
    if (!isAssets404 && 'target' in event) {
      const target = event.target as unknown as { as: string; localName: string } | null
      const localName = target?.as || target?.localName || ''
      isAssets404 = ['script', 'link'].includes(localName)
    }
    if (isAssets404){
      handleAssets404()
      return
    };
  }

  const whiteListMessage = [
    'ResizeObserver',
  ]
  const isAssets = whiteListMessage.some(item => message?.includes(item))
  if (isAssets){
    return
  };

  // 其他情况提示
  if (message && !settingsStore.settings.errorConfig.hideErrorAlert) {
    settingsStore.settings.errorConfig.onErrorAlert
      ? settingsStore.settings.errorConfig.onErrorAlert(message)
      : ElMessage({ type: 'error', message });
  }
};

const options: PluginOptions = {
  onInit: () => {
    addErrorHandler((event) => {
      errorHandler(event);
    });

    // 静态资源 404 时自动刷新页面(防止蓝绿环境下因为切换灰度导致的资源丢失)
    // 必须在捕获阶段执行，否则无法监听到
    window.addEventListener('error', (event) => {
      errorHandler(event);
    }, true)

    window.addEventListener('error', (event) => {
      event.preventDefault();
      errorHandler(event);
    });

    window.addEventListener('unhandledrejection', (event) => {
      event.preventDefault();
      errorHandler(event);
    });
  },
  onVuePluginRegister: async ({ app }) => {
    app.config.errorHandler = (err) => {
      errorHandler(err as Error);
    };
  },
};

export default options;
